import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import MemberRoleSelect from '../../../../components/MemberRoleSelect';
import { createOccupant } from '../../../../redux/actions/SocietyPortal/OccupantAction/OccupantAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { GENDER_OPTIONS, ENTITY_TYPE_OPTIONS, gstinError } from './ownerFormOptions';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_MEMBERS_OWNERS } from '../../../../Routing/routes';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';

const AddOwner = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { createLoading } = useSelector(state => state.OccupantReducer);
    const { wingsDropdown, wingsDropdownLoading, flatsDropdown, flatsDropdownLoading } = useSelector(state => state.WingsAndFlatsReducers);
    const { can } = usePermissions();
    // Anyone who may add / edit members picks the login role; the options
    // (and the API) limit non-admins to member roles.
    const canPickRole = can(PERMISSIONS.USER_MANAGE_ROLES) || can(PERMISSIONS.OCCUPANT_CREATE);

    const wings = wingsDropdown || [];
    const flats = flatsDropdown || [];

    const [formData, setFormData] = useState({
        entity_type: 'I',
        first_name: '',
        last_name: '',
        company_name: '',
        gender: '',
        date_of_birth: '',
        email: '',
        secondary_email: '',
        phone: '',
        secondary_phone: '',
        pan_no: '',
        gstin_no: '',
        van: '',
        address: '',
        billing_address: '',
        shipping_address: '',
        same_as_billing: false,
        wing_id: 'all',
        flat_id: 'all',
        ownership_share: '',
        start_date: '',
        role_id: '',
    });
    const [errors, setErrors] = useState({});
    // Existing owners for the currently-picked flat. Loaded on the fly so we
    // can (a) warn the user they're adding a co-owner and (b) auto-fill the
    // remaining share instead of forcing them to do the math.
    const [existingOwners, setExistingOwners] = useState([]);
    const [existingOwnersLoading, setExistingOwnersLoading] = useState(false);

    const usedShare = useMemo(
        () => (existingOwners || []).reduce((sum, o) => sum + (parseFloat(o.ownership_share) || 0), 0),
        [existingOwners]
    );
    const remainingShare = Math.max(0, 100 - usedShare);

    useEffect(() => {
        const societyId = selectedSocietyStorer.getSocietyId();
        if (societyId) {
            dispatch(getWingsDropdown({ society_id: societyId }));
        }
    }, [dispatch]);

    const today = localDateString();

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
            // Keep shipping in sync with billing when the toggle is on.
            if (name === 'billing_address' && prev.same_as_billing) {
                next.shipping_address = value;
            }
            if (name === 'same_as_billing' && checked) {
                next.shipping_address = prev.billing_address;
            }
            return next;
        });
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleWingChange = (value) => {
        setFormData(prev => ({ ...prev, wing_id: value, flat_id: 'all' }));
        if (errors.wing_id) setErrors(prev => ({ ...prev, wing_id: '' }));
        if (value !== 'all') {
            dispatch(getFlatsDropdown({ wing_id: value }));
        }
    };

    const handleFlatChange = (value) => {
        setFormData(prev => ({ ...prev, flat_id: value }));
        if (errors.flat_id) setErrors(prev => ({ ...prev, flat_id: '' }));

        // Reset then load co-owner context for the picked flat. Using the
        // large-limit list endpoint keeps this one roundtrip regardless of
        // how many owners the flat has.
        setExistingOwners([]);
        if (!value || value === 'all') return;
        setExistingOwnersLoading(true);
        axiosInstance.get(apiEndpoints.GET_OCCUPANTS_API, {
            params: {
                type: 'owner',
                flat_id: value,
                page: 1,
                limit: 50
            }
        })
            .then((res) => {
                const rows = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
                setExistingOwners(rows);
                // If we haven't touched the share input yet, auto-fill it to
                // the remainder — most common case is "give me what's left".
                const usedSoFar = rows.reduce((s, o) => s + (parseFloat(o.ownership_share) || 0), 0);
                const remaining = Math.max(0, 100 - usedSoFar);
                setFormData(prev => (
                    prev.ownership_share === '' || prev.ownership_share === '100'
                        ? { ...prev, ownership_share: String(remaining || '') }
                        : prev
                ));
            })
            .catch(() => setExistingOwners([]))
            .finally(() => setExistingOwnersLoading(false));
    };

    const handleDropdownChange = (name) => (value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    }
    const handleRoleChange = (value) => {
        setFormData(prev => ({ ...prev, role_id: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        const isIndividual = formData.entity_type === 'I';
        if (isIndividual) {
            if (!formData.first_name.trim()) newErrors.first_name = 'First Name is required';
            if (formData.date_of_birth && formData.date_of_birth > today) {
                newErrors.date_of_birth = 'Date of birth cannot be in the future';
            }
        } else {
            if (!formData.company_name.trim()) newErrors.company_name = 'Company Name is required';
        }
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone is required';
        } else if (!/^\d{10}$/.test(formData.phone.trim())) {
            newErrors.phone = 'Phone must be exactly 10 digits';
        }
        // Email is the owner's login ID. Without it the backend used to derive a
        // "<phone>@login.society.local" placeholder that nobody can receive mail
        // on, so it is now required (occupantValidator.js enforces the same).
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            newErrors.email = 'Invalid email format';
        }
        if (formData.secondary_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.secondary_email)) {
            newErrors.secondary_email = 'Invalid email format';
        }
        if (formData.secondary_phone && formData.secondary_phone.length > 15) {
            newErrors.secondary_phone = 'Phone must be at most 15 characters';
        }
        if (formData.pan_no && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan_no.trim())) {
            newErrors.pan_no = 'Invalid PAN (e.g., ABCDE1234F)';
        }
        // GST is optional; when given it must be a valid GSTIN carrying the PAN.
        const gstErr = gstinError(formData.gstin_no, formData.pan_no);
        if (gstErr) newErrors.gstin_no = gstErr;
        if (formData.wing_id === 'all') newErrors.wing_id = 'Wing is required';
        if (formData.flat_id === 'all') newErrors.flat_id = 'Flat is required';
        // start_date is required by the backend (occupantValidator.js), so
        // enforce it client-side too — otherwise users hit a 400 after
        // clicking Save with an empty date field.
        if (!formData.start_date) {
            newErrors.start_date = 'Start Date is required';
        }
        if (!formData.ownership_share) {
            newErrors.ownership_share = 'Ownership share is required';
        } else {
            const share = Number(formData.ownership_share);
            if (!Number.isInteger(share) || share < 1 || share > 100) {
                newErrors.ownership_share = 'Must be between 1 and 100';
            } else if (existingOwners.length > 0 && share > remainingShare) {
                // Guard client-side too so the user sees the error before the
                // server responds with the same message.
                newErrors.ownership_share = `Only ${remainingShare}% is available for this flat`;
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const societyId = selectedSocietyStorer.getSocietyId();
        const isIndividual = formData.entity_type === 'I';
        const payload = {
            society_id: societyId,
            type: 'OWNER',
            entity_type: formData.entity_type,
            email: formData.email.trim(),
            phone: formData.phone,
            wing_id: formData.wing_id,
            flat_id: formData.flat_id,
            ownership_share: Number(formData.ownership_share),
        };
        if (isIndividual) {
            payload.first_name = formData.first_name;
            payload.last_name = formData.last_name;
            if (formData.gender) payload.gender = formData.gender;
            if (formData.date_of_birth) payload.date_of_birth = formData.date_of_birth;
        } else {
            payload.company_name = formData.company_name;
        }
        if (formData.start_date) payload.start_date = formData.start_date;
        if (formData.secondary_email) payload.secondary_email = formData.secondary_email;
        if (formData.secondary_phone) payload.secondary_phone = formData.secondary_phone;
        if (formData.pan_no) payload.pan_no = formData.pan_no.trim().toUpperCase();
        if (formData.gstin_no) payload.gstin_no = formData.gstin_no.trim().toUpperCase();
        if (formData.van) payload.van = formData.van.trim();
        if (formData.address) payload.address = formData.address;
        if (formData.billing_address) payload.billing_address = formData.billing_address;
        if (formData.shipping_address) payload.shipping_address = formData.shipping_address;
        if (canPickRole && formData.role_id) payload.role_id = Number(formData.role_id);

        payload.onSuccess = () => {
            navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS_OWNERS);
        };

        dispatch(createOccupant(payload));
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS_OWNERS)}
                        className="tw-p-2 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-600"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Add New Owner</h1>
                        <p className="tw-text-xs tw-text-gray-500">Fill in the owner details below</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MEMBERS_OWNERS)}
                        className="tw-px-6 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="addOwnerForm"
                        disabled={createLoading}
                        className="tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg tw-flex tw-items-center tw-gap-1.5 disabled:tw-opacity-50 tw-transition-colors"
                    >
                        {createLoading ? (
                            <>
                                <Icon icon="svg-spinners:ring-resize" className="tw-text-base" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Icon icon="mingcute:save-line" className="tw-text-base" />
                                Save
                            </>
                        )}
                    </button>
                </div>
            </div>

            <form id="addOwnerForm" onSubmit={handleSubmit}>
                {/* Basic Info */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:account-outline" className="tw-text-blue-500" />
                        Basic Info
                    </h2>
                    <div className="tw-mb-3 tw-flex tw-items-center tw-gap-4">
                        <span className="tw-text-xs tw-font-medium tw-text-gray-600">Entity Type</span>
                        {ENTITY_TYPE_OPTIONS.map(opt => (
                            <label
                                key={opt.value}
                                /* Bare radios gave no hover or selected feedback, so it was hard
                                   to tell which entity type was active. Chip styling: a hover
                                   border/tint on the whole label, and a filled state for the
                                   current choice. */
                                className={`tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-cursor-pointer tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-transition-colors ${
                                    formData.entity_type === opt.value
                                        ? 'tw-border-blue-500 tw-bg-blue-50 tw-text-blue-700 tw-font-medium'
                                        : 'tw-border-gray-200 tw-text-gray-700 hover:tw-border-blue-400 hover:tw-bg-blue-50/60'
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="entity_type"
                                    value={opt.value}
                                    checked={formData.entity_type === opt.value}
                                    onChange={handleChange}
                                    className="tw-cursor-pointer"
                                />
                                <span>{opt.label}</span>
                            </label>
                        ))}
                    </div>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        {formData.entity_type === 'I' ? (
                            <>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                        First Name <span className="tw-text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.first_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                        placeholder="Enter first name"
                                        maxLength={50}
                                    />
                                    {errors.first_name && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.first_name}</p>}
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.last_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                        placeholder="Enter last name"
                                        maxLength={50}
                                    />
                                    {errors.last_name && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.last_name}</p>}
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Gender</label>
                                    <SearchableDropdown
                                        value={formData.gender}
                                        onChange={handleDropdownChange('gender')}
                                        options={GENDER_OPTIONS}
                                        valueKey="value"
                                        labelKey="label"
                                        placeholder="Select Gender"
                                        className="tw-w-full"
                                    />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Date of Birth</label>
                                    <input
                                        type="date"
                                        name="date_of_birth"
                                        value={formData.date_of_birth}
                                        onChange={handleChange}
                                        max={today}
                                        className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.date_of_birth ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                    />
                                    {errors.date_of_birth && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.date_of_birth}</p>}
                                </div>
                            </>
                        ) : (
                            <div className="md:tw-col-span-2">
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                    Company Name <span className="tw-text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleChange}
                                    className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.company_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                    placeholder="Enter company name"
                                    maxLength={100}
                                />
                                {errors.company_name && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.company_name}</p>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Contact */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:phone-outline" className="tw-text-blue-500" />
                        Contact
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Email <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.email ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Email Address"
                                maxLength={100}
                            />
                            {errors.email && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.email}</p>}
                            <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5">Used as the owner's login ID</p>
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Secondary Email</label>
                            <input
                                type="email"
                                name="secondary_email"
                                value={formData.secondary_email}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.secondary_email ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Secondary Email"
                                maxLength={100}
                            />
                            {errors.secondary_email && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.secondary_email}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Phone <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.phone ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Phone Number"
                                maxLength={15}
                            />
                            {errors.phone && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.phone}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Secondary Phone</label>
                            <input
                                type="text"
                                name="secondary_phone"
                                value={formData.secondary_phone}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.secondary_phone ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="Secondary Phone"
                                maxLength={15}
                            />
                            {errors.secondary_phone && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.secondary_phone}</p>}
                        </div>
                    </div>
                </div>

                {/* Tax Details */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:card-account-details-outline" className="tw-text-purple-500" />
                        Tax Details
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">PAN Number</label>
                            <input
                                type="text"
                                name="pan_no"
                                value={formData.pan_no}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm tw-uppercase focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.pan_no ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="ABCDE1234F"
                                maxLength={10}
                            />
                            {errors.pan_no && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.pan_no}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                GST Number <span className="tw-text-gray-400 tw-font-normal">(Optional)</span>
                            </label>
                            <input
                                type="text"
                                name="gstin_no"
                                value={formData.gstin_no}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm tw-uppercase focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.gstin_no ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="22ABCDE1234F1Z5"
                                maxLength={15}
                            />
                            {errors.gstin_no && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.gstin_no}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                VAN <span className="tw-text-gray-400 tw-font-normal">(Virtual Account Number, Optional)</span>
                            </label>
                            <input
                                type="text"
                                name="van"
                                value={formData.van}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.van ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder="e.g. VAN123456789"
                                maxLength={50}
                            />
                            {errors.van && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.van}</p>}
                        </div>
                    </div>
                </div>

                {/* Addresses */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:map-marker-outline" className="tw-text-green-500" />
                        Addresses
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Primary Address</label>
                            <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                rows={3}
                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-resize-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                placeholder="Enter primary address"
                            />
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Billing Address</label>
                            <textarea
                                name="billing_address"
                                value={formData.billing_address}
                                onChange={handleChange}
                                rows={3}
                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-resize-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                                placeholder="Enter billing address"
                            />
                        </div>
                        <div>
                            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600">Shipping Address</label>
                                <label className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-600 tw-cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="same_as_billing"
                                        checked={formData.same_as_billing}
                                        onChange={handleChange}
                                        className="tw-cursor-pointer"
                                    />
                                    Same as billing
                                </label>
                            </div>
                            <textarea
                                name="shipping_address"
                                value={formData.shipping_address}
                                onChange={handleChange}
                                rows={3}
                                disabled={formData.same_as_billing}
                                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-resize-none focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-cursor-not-allowed"
                                placeholder="Enter shipping address"
                            />
                        </div>
                    </div>
                </div>

                {/* Unit Details */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:home-outline" className="tw-text-blue-500" />
                        Unit Details
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Wing <span className="tw-text-red-500">*</span>
                            </label>
                            <SearchableDropdown
                                value={formData.wing_id}
                                onChange={handleWingChange}
                                options={wings}
                                valueKey="wing_id"
                                labelKey="title"
                                placeholder="Select Wing"
                                loading={wingsDropdownLoading}
                                className="tw-w-full"
                            />
                            {errors.wing_id && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.wing_id}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Flat <span className="tw-text-red-500">*</span>
                            </label>
                            <SearchableDropdown
                                value={formData.flat_id}
                                onChange={handleFlatChange}
                                options={flats}
                                valueKey="flat_id"
                                labelKey="flat_number"
                                placeholder="Select Flat"
                                loading={flatsDropdownLoading}
                                className="tw-w-full"
                            />
                            {errors.flat_id && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.flat_id}</p>}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Ownership Share (%) <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="ownership_share"
                                value={formData.ownership_share}
                                onChange={handleChange}
                                min={1}
                                max={remainingShare > 0 ? remainingShare : 100}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.ownership_share ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                placeholder={existingOwners.length > 0 ? `up to ${remainingShare}` : 'e.g. 100'}
                            />
                            {errors.ownership_share && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.ownership_share}</p>}
                            {existingOwners.length > 0 && (
                                <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">
                                    {usedShare}% held by {existingOwners.length} existing owner{existingOwners.length === 1 ? '' : 's'} — {remainingShare}% available.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                Start Date <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.start_date ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                            />
                            {errors.start_date && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.start_date}</p>}
                        </div>
                        {canPickRole && (
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Login Role</label>
                                <MemberRoleSelect
                                    value={formData.role_id}
                                    onChange={handleRoleChange}
                                    placeholder="Default: Resident"
                                    className="tw-w-full"
                                />
                                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5">Leave blank to default to Resident</p>
                            </div>
                        )}
                    </div>
                    {/* Co-owner context: shown only when the picked flat already has
                        owners. Lets the user confirm they're intentionally adding a
                        co-owner rather than duplicating a record. */}
                    {formData.flat_id && formData.flat_id !== 'all' && (existingOwnersLoading || existingOwners.length > 0) && (
                        <div className={`tw-mt-3 tw-p-3 tw-rounded-lg tw-border ${remainingShare === 0 ? 'tw-bg-rose-50 tw-border-rose-200' : 'tw-bg-blue-50 tw-border-blue-200'}`}>
                            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                                <Icon
                                    icon={remainingShare === 0 ? 'mdi:alert-circle-outline' : 'mdi:information-outline'}
                                    className={remainingShare === 0 ? 'tw-text-rose-600' : 'tw-text-blue-600'}
                                />
                                <span className={`tw-text-xs tw-font-semibold ${remainingShare === 0 ? 'tw-text-rose-700' : 'tw-text-blue-700'}`}>
                                    {existingOwnersLoading
                                        ? 'Checking existing owners…'
                                        : remainingShare === 0
                                            ? `This flat is fully owned (${usedShare}%). No more owners can be added.`
                                            : `Adding as co-owner — ${existingOwners.length} existing owner${existingOwners.length === 1 ? '' : 's'} hold ${usedShare}%.`}
                                </span>
                            </div>
                            {!existingOwnersLoading && existingOwners.length > 0 && (
                                <ul className="tw-mt-1.5 tw-space-y-0.5">
                                    {existingOwners.map((o) => {
                                        const entity = o.entity || {};
                                        const label = entity.entity_type === 'C'
                                            ? (entity.company_name || 'Company')
                                            : [entity.first_name, entity.last_name].filter(Boolean).join(' ').trim() || entity.email || `Owner #${o.entity_id}`;
                                        return (
                                            <li key={o.flat_ownership_id} className="tw-text-[11px] tw-text-gray-700 tw-flex tw-items-center tw-gap-1.5">
                                                <Icon icon="mdi:account-outline" className="tw-text-gray-400" />
                                                <span>{label}</span>
                                                <span className="tw-text-gray-500">— {parseFloat(o.ownership_share) || 0}%</span>
                                                {entity.is_primary === 1 && (
                                                    <span className="tw-ml-1 tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-blue-700 tw-bg-blue-100 tw-rounded tw-px-1">Primary</span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

            </form>
        </>
    );
};

export default AddOwner;
